import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { 
  Plane, Home, BookOpen, GraduationCap, Layers, Timer,
  Mic, MessageSquare, Users, AlertTriangle, Trophy,
  ChevronLeft, ChevronRight, Menu, X, Globe, Target,
  Zap, Award, Settings, LogOut, User, Briefcase, Building2,
  Newspaper, Scissors, FileText, Heart, Map, Volume2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  {
    id: 'home',
    label: 'Ana Sayfa',
    icon: Home,
    path: '/dashboard',
    color: 'text-blue-500'
  },
  {
    id: 'roadmap',
    label: 'Öğrenme Yolu',
    icon: Map,
    path: '/learning-path',
    color: 'text-indigo-500',
    badge: '🎯 A1-C2'
  },
  {
    id: 'grammar-training',
    label: 'Gramer Eğitimleri',
    icon: Globe,
    color: 'text-emerald-500',
    children: [
      { id: 'tenses', label: 'Zamanlar Eğitimi', icon: Timer, path: '/exercises?tab=tenses', subLabel: 'English Tenses' },
      { id: 'basic-grammar', label: 'Basic English Grammar', icon: GraduationCap, path: '/exercises?tab=basic' },
      { id: 'grammar-practice', label: 'Gramer Pratik', icon: Target, path: '/exercises?tab=practice', subLabel: 'Grammar Practice' },
      { id: 'vocabulary', label: 'Kelime Egzersizleri', icon: BookOpen, path: '/exercises?tab=vocabulary' },
    ]
  },
  {
    id: 'modules',
    label: 'Cabin Crew',
    icon: BookOpen,
    color: 'text-purple-500',
    children: [
      { id: 'comprehensive', label: 'Cabin Crew English', icon: GraduationCap, path: '/cabin-crew/modules' },
      { id: 'flashcards', label: 'Flashcard', icon: Layers, path: '/cabin-crew/flashcards' },
      { id: 'dragdrop', label: 'Sürükle & Bırak', icon: Target, path: '/cabin-crew/drag-drop' },
      { id: 'timed', label: 'Zamanlı Yarışma', icon: Timer, path: '/cabin-crew/timed-challenge' },
      { id: 'voice', label: 'Sesli Pratik', icon: Mic, path: '/cabin-crew/voice-practice' },
      { id: 'roleplay', label: 'Rol-Play', icon: MessageSquare, path: '/cabin-crew/roleplay' },
      { id: 'interview', label: 'Mülakat', icon: Users, path: '/cabin-crew/interview' },
      { id: 'emergency', label: 'Acil Durum', icon: AlertTriangle, path: '/cabin-crew/emergency' },
    ]
  },
  {
    id: 'skills',
    label: '4 Beceri Testleri',
    icon: Zap,
    color: 'text-emerald-500',
    children: [
      { id: 'speaking', label: 'Konuşma (Speaking)', icon: Mic, path: '/speaking-practice' },
      { id: 'reading', label: 'Okuma (Reading)', icon: BookOpen, path: '/reading-test' },
      { id: 'writing', label: 'Yazma (Writing)', icon: FileText, path: '/writing-test' },
      { id: 'listening', label: 'Dinleme (Listening)', icon: Volume2, path: '/listening-test' },
    ]
  },
  {
    id: 'my-exams',
    label: 'Sınavlarım',
    icon: Award,
    path: '/cabin-crew/my-exams',
    color: 'text-amber-500'
  },
  {
    id: 'career',
    label: 'Kariyer Yönetimi',
    icon: Briefcase,
    color: 'text-rose-500',
    children: [
      { id: 'cv', label: 'CV Hazırlama', icon: FileText, path: '/cabin-crew/career/cv' },
      { id: 'fitness', label: 'Fiziksel Testler', icon: Heart, path: '/cabin-crew/career/fitness' },
      { id: 'grooming', label: 'İmaj & Grooming', icon: Scissors, path: '/cabin-crew/career/grooming' },
    ]
  },
  {
    id: 'airlines',
    label: 'Havayolu Bilgi Bankası',
    icon: Building2,
    path: '/cabin-crew/airlines',
    color: 'text-cyan-500'
  },
  {
    id: 'progress',
    label: 'İlerleme',
    icon: Target,
    path: '/cabin-crew/progress',
    color: 'text-green-500'
  },
  {
    id: 'achievements',
    label: 'Başarılar',
    icon: Trophy,
    path: '/cabin-crew/achievements',
    color: 'text-amber-500'
  }
];

export default function CabinCrewSidebar({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(AuthContext);
  const [expandedMenus, setExpandedMenus] = useState(['grammar-training', 'modules']);

  // Demo kullanıcılar için kilitli menü öğeleri
  const isDemo = user?.is_demo || false;
  const demoLockedItems = ['comprehensive', 'dragdrop', 'interview', 'emergency', 'cv', 'fitness', 'grooming', 'speaking', 'reading', 'writing', 'listening', 'grammar-training', 'languages', 'level-test', 'grammar'];
  const demoAllowedVoiceCategories = ['welcome', 'landing'];
  const demoAllowedRoleplayScenarios = ['greeting'];

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      localStorage.removeItem('session_token');
      setUser(null);
      toast.success('Çıkış yapıldı');
      navigate('/');
    } catch (error) {
      // Hata olsa bile çıkış yap
      localStorage.removeItem('session_token');
      setUser(null);
      navigate('/');
    }
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isActive = (path) => location.pathname === path;
  const isChildActive = (children) => children?.some(child => location.pathname === child.path);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-50 h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900
          transition-all duration-300 ease-in-out shadow-2xl
          ${isOpen ? 'w-64' : 'w-0 lg:w-20'}
          overflow-hidden
        `}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <div className={`flex items-center gap-3 ${!isOpen && 'lg:justify-center lg:w-full'}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Plane className="w-6 h-6 text-white" />
            </div>
            {isOpen && (
              <div className="overflow-hidden">
                <h1 className="font-bold text-white text-lg whitespace-nowrap">AirLingo</h1>
                <p className="text-xs text-slate-400 whitespace-nowrap">Kabin Eğitimi</p>
              </div>
            )}
          </div>
          
          {/* Close button for mobile */}
          <button 
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100%-8rem)]">
          {menuItems.map((item) => {
            const isParentLocked = isDemo && demoLockedItems.includes(item.id);
            return (
            <div key={item.id}>
              {/* Parent Item */}
              <button
                onClick={() => {
                  if (isParentLocked) {
                    toast.error('Bu özellik tam sürümde mevcuttur');
                    return;
                  }
                  if (item.children) {
                    if (isOpen) {
                      toggleMenu(item.id);
                    } else {
                      setIsOpen(true);
                      setExpandedMenus(prev => [...prev, item.id]);
                    }
                  } else {
                    navigate(item.path);
                    if (window.innerWidth < 1024) setIsOpen(false);
                  }
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all
                  ${isParentLocked 
                    ? 'opacity-50 cursor-not-allowed text-slate-500'
                    : isActive(item.path) || isChildActive(item.children)
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }
                  ${!isOpen && 'lg:justify-center lg:px-2'}
                `}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${!isParentLocked && !isActive(item.path) && !isChildActive(item.children) ? item.color : ''}`} />
                {isOpen && (
                  <>
                    <span className="flex-1 text-left text-sm font-medium whitespace-nowrap">{item.label}</span>
                    {isParentLocked && <span className="text-xs">🔒</span>}
                    {!isParentLocked && item.badge && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-medium">
                        {item.badge}
                      </span>
                    )}
                    {!isParentLocked && item.children && (
                      <ChevronRight 
                        className={`w-4 h-4 transition-transform ${expandedMenus.includes(item.id) ? 'rotate-90' : ''}`}
                      />
                    )}
                  </>
                )}
              </button>

              {/* Children */}
              {item.children && isOpen && expandedMenus.includes(item.id) && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-700 pl-3">
                  {item.children.map((child) => {
                    const isLocked = isDemo && demoLockedItems.includes(child.id);
                    return (
                    <button
                      key={child.id}
                      onClick={() => {
                        if (isLocked) {
                          toast.error('Bu özellik tam sürümde mevcuttur');
                          return;
                        }
                        navigate(child.path);
                        if (window.innerWidth < 1024) setIsOpen(false);
                      }}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm
                        ${isLocked 
                          ? 'opacity-50 cursor-not-allowed text-slate-500'
                          : isActive(child.path)
                            ? 'bg-blue-600/20 text-blue-400 font-medium'
                            : 'text-slate-400 hover:bg-slate-700/30 hover:text-slate-200'
                        }
                      `}
                    >
                      <child.icon className="w-4 h-4" />
                      <span className="whitespace-nowrap">{child.label}</span>
                      {isLocked && <span className="ml-auto text-xs">🔒</span>}
                    </button>
                  )})}
                </div>
              )}
            </div>
          )})}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-700 bg-slate-900/50">
          <div className={`flex ${isOpen ? 'justify-between' : 'lg:justify-center'} items-center`}>
            {isOpen && (
              <button 
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <User className="w-5 h-5" />
                <span className="text-sm">Profil</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-red-400 transition-colors"
              title="Çıkış"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Toggle Button (Desktop) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          hidden lg:flex fixed top-4 z-50 p-2 bg-slate-800 hover:bg-slate-700 
          rounded-full shadow-lg text-white transition-all duration-300
          ${isOpen ? 'left-[252px]' : 'left-[68px]'}
        `}
      >
        {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`
          lg:hidden fixed top-4 left-4 z-30 p-3 bg-gradient-to-r from-blue-600 to-purple-600
          rounded-xl shadow-lg text-white
          ${isOpen ? 'hidden' : 'flex'}
        `}
      >
        <Menu className="w-5 h-5" />
      </button>
    </>
  );
}
